import React, { useState, useContext, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Upload, Delete } from 'baseui/icon';
import { Button, SHAPE } from 'baseui/button';
import { Card, StyledBody } from 'baseui/card';
import { Table } from 'baseui/table-semantic';
import { AppContext } from 'commons-ui';
import _get from 'lodash/get';

import { CsvUploadPreviewProps, ParseCsvResult, InvalidCsvRow } from './CsvUploadPreview.types';
import { useCsvUploadPreviewStyles } from './CsvUploadPreview.styles';

const DEFAULT_PREVIEW_LIMIT = 100;

function CsvUploadPreview<TRow = Record<string, any>>(props: CsvUploadPreviewProps<TRow>) {
	const {
		name,
		control,
		defaultValue,
		disabled,
		parseCsv,
		columns,
		rowFormatter,
		onValidRows,
		uploadSuccessMessage = (count: number) => `Successfully parsed ${count} rows from CSV`,
		uploadErrorMessage = 'Failed to parse CSV file',
		previewLimit = DEFAULT_PREVIEW_LIMIT,
		...otherProps
	} = props as any;

	const { showAlert } = useContext(AppContext);
	const { disabledStyle, containerStyle, fileNameStyle, overflowAuto, infoText, css } = useCsvUploadPreviewStyles();

	const [csvData, setCsvData] = useState<TRow[]>([]);
	const [invalidRows, setInvalidRows] = useState<InvalidCsvRow[]>([]);
	const [showPreview, setShowPreview] = useState(false);

	const handleFileUpload = useCallback(
		async (file: File, onChange: (value: File | null) => void) => {
			if (!file) return;
			try {
				const { validRows, invalidRows: parsedInvalid } = (await parseCsv(file)) as ParseCsvResult<TRow>;
				setCsvData(validRows);
				setInvalidRows(parsedInvalid);
				setShowPreview(true);
				onChange(file);
				if (typeof onValidRows === 'function') {
					onValidRows(validRows, onChange);
				}
				showAlert(uploadSuccessMessage(validRows.length), { type: 'success' });
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : uploadErrorMessage;
				showAlert(errorMessage, { type: 'error' });
				onChange(null);
				setCsvData([]);
				setInvalidRows([]);
				setShowPreview(false);
				if (typeof onValidRows === 'function') {
					onValidRows([], onChange);
				}
			}
		},
		[onValidRows, parseCsv, showAlert, uploadSuccessMessage, uploadErrorMessage]
	);

	const handleRemoveFile = (onChange: (value: File | null) => void) => {
		onChange(null);
		setCsvData([]);
		setInvalidRows([]);
		setShowPreview(false);
		if (typeof onValidRows === 'function') {
			onValidRows([], onChange);
		}
	};

	const previewData = useMemo(() => {
		const limitedRows = csvData.slice(0, previewLimit);
		return limitedRows.map((row, index) => {
			if (rowFormatter) {
				return rowFormatter(row, index);
			}
			return Object.values(row as Record<string, unknown>).map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)));
		});
	}, [csvData, previewLimit, rowFormatter]);

	const iconStyleProps = { className: disabled ? disabledStyle : css({ cursor: 'pointer' }) };

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={defaultValue && _get(defaultValue, ['field', ...(defaultValue as any).path])}
			render={({ field: { onChange, value } }) => (
				<div>
					<div className={disabled ? [containerStyle, disabledStyle].join(' ') : containerStyle}>
						<label htmlFor={`csv-input-${name}`}>
							<Upload size={40} {...iconStyleProps} />
						</label>
						<input
							id={`csv-input-${name}`}
							type='file'
							className={css({ display: 'none' })}
							accept='.csv'
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) {
									handleFileUpload(file, onChange);
									e.target.value = '';
								}
							}}
							disabled={disabled}
						/>
						{value && (
							<div className={fileNameStyle}>
								{value.name} ({csvData.length} rows)
								<Button
									shape={SHAPE.circle}
									$size='mini'
									onClick={() => handleRemoveFile(onChange)}
									{...iconStyleProps}>
									<Delete size={20} {...iconStyleProps} />
								</Button>
							</div>
						)}
					</div>

					{showPreview && csvData.length > 0 && (
						<Card
							overrides={{
								Root: {
									style: {
										marginTop: '20px',
										marginBottom: '20px'
									}
								}
							}}>
							<StyledBody>
								<h4>CSV Preview ({csvData.length} rows)</h4>
								<div className={overflowAuto}>
									<Table columns={[...columns]} data={previewData} />
								</div>
								{csvData.length > previewLimit && (
									<div className={infoText}>
										Showing first {previewLimit} rows. Total: {csvData.length} rows
									</div>
								)}
							</StyledBody>
						</Card>
					)}

					{invalidRows.length > 0 && (
						<div style={{ marginTop: '10px', color: '#d9534f' }}>
							<p>
								Ignored {invalidRows.length} invalid row{invalidRows.length > 1 ? 's' : ''}. Reasons:
							</p>
							<ul style={{ maxHeight: '120px', overflowY: 'auto' }}>
								{invalidRows.slice(0, 10).map((row) => (
									<li key={row.index}>
										Row {row.index}: {row.reason}
									</li>
								))}
								{invalidRows.length > 10 && <li>…and {invalidRows.length - 10} more</li>}
							</ul>
						</div>
					)}
				</div>
			)}
			{...otherProps}
		/>
	);
}

export default CsvUploadPreview;
